import { CJEvent, CJEventApi, CJEventType } from '@teetsh/app/src/types/event';
import _omit from 'lodash-es/omit';
import {
  deserializeItems,
  serializeItems,
} from '@teetsh/app/src/queries/serializer';
import differenceInMinutes from '@teetsh/app/src/utils/time/differenceInMinutes';
import startOfMinute from '@teetsh/app/src/utils/time/startOfMinute';
import addMinutes from '@teetsh/app/src/utils/time/addMinutes';
import parseISO from '@teetsh/app/src/utils/time/parseISO';
import formatISO from '@teetsh/app/src/utils/time/formatISO';
import serializeDateToServer from '@teetsh/app/src/utils/time/serializeDateToServer';
import deserializeDateFromServer from '@teetsh/app/src/utils/time/deserializeDateFromServer';
import { CJSeanceUpdateView } from '@teetsh/app/src/containers/CahierJournal/queries/types/CJSeanceUpdateView';
import emptyBo from '@teetsh/app/src/components/Bo/emptyBo';
import { CJAtelier, CJAtelierApi } from 'types/cj';

const defaultToEmptyArray = item => {
  if (!item) {
    return [];
  }
  return item;
};

function deserializeCJAtelier(atelier: CJAtelierApi): CJAtelier {
  return {
    ..._omit(atelier, 'pendingEphemeralGroups'),
    bo: {
      competence: serializeItems(defaultToEmptyArray(atelier.competence)),
      goals: serializeItems(defaultToEmptyArray(atelier.goals)),
      domaine: serializeItems(defaultToEmptyArray(atelier.domaine)),
      matiere: serializeItems(defaultToEmptyArray(atelier.matiere)),
      cycle: atelier.cycle,
    },
    materials: serializeItems(atelier.materials),
  };
}

function serializeCJAtelier(atelier: CJAtelier): CJAtelierApi {
  const bo = atelier.bo || emptyBo;
  return {
    ...atelier,
    cycle: bo.cycle,
    competence: deserializeItems(bo.competence),
    goals: deserializeItems(bo.goals),
    domaine: deserializeItems(bo.domaine),
    matiere: deserializeItems(bo.matiere),
    materials: deserializeItems(atelier.materials),
    pendingEphemeralGroups: atelier.pendingEphemeralGroups,
  };
}

export function serializeCJSeanceUpdateView(
  seance: CJEvent,
  tz: string,
): CJSeanceUpdateView {
  const eventBo = seance.bo || emptyBo;
  return {
    id: seance.id,
    classroomIds: seance.classroomIds,
    flow: seance.flow,
    status: seance.status,
    lasting: differenceInMinutes(seance.end, seance.start),
    start: formatISO(serializeDateToServer(seance.start, tz)),
    title: seance.title,
    matiere: deserializeItems(eventBo.matiere),
    domaine: deserializeItems(eventBo.domaine),
    competence: deserializeItems(eventBo.competence),
    goals: deserializeItems(eventBo.goals),
    materials: deserializeItems(seance.materials),
    type: seance.type,
    tagId: seance.tag?.id,
    attachments: seance.attachments || [],
    linkedFichesDePrep: seance.linkedFichesDePrep,
    programmationItemId: seance.programmationItemId,
    order: seance.order,
    cycle: eventBo.cycle || seance.cycle,
    customFields: (seance.customFields || []).map(customField => ({
      ...customField,
      items: deserializeItems(customField.items),
    })),
    ateliers: (seance.ateliers || []).map(a => serializeCJAtelier(a)),
  };
}

export function cleanPendingEphemeralGroups(event: CJEvent): CJEvent {
  return {
    ...event,
    ateliers: (event.ateliers || []).map(atelier => ({
      ...atelier,
      pendingEphemeralGroups: undefined,
    })),
  };
}

export function deserializeEvent(event: CJEventApi, tz: string): CJEvent {
  const { competence, goals, domaine, matiere, cycle } = event;
  const eventStart = parseISO(event.start);
  const start = deserializeDateFromServer(eventStart, tz);
  return {
    tag: event.tag,
    createdAt: parseISO(event.createdAt),
    flow: event.flow,
    id: event.id,
    classroomIds: event.classroomIds,
    cycle: event.cycle,
    linkedFichesDePrep: event.linkedFichesDePrep,
    programmationItemId: event.programmationItemId,
    attachments: event.attachments,
    isDemo: event.isDemo,
    order: event.order,
    status: event.status,
    title: event.title,
    type: event.type as CJEventType,
    bo: {
      competence: serializeItems(defaultToEmptyArray(competence)),
      goals: serializeItems(defaultToEmptyArray(goals)),
      domaine: serializeItems(defaultToEmptyArray(domaine)),
      matiere: serializeItems(defaultToEmptyArray(matiere)),
      cycle,
    },
    materials: serializeItems(event.materials),
    customFields: (event.customFields || []).map(customField => ({
      ...customField,
      items: serializeItems(customField.items),
    })),
    start: startOfMinute(start),
    end: addMinutes(start, event.lasting),
    ateliers: (event.ateliers || []).map(a => deserializeCJAtelier(a)),
  };
}
